import { supabase, q } from '../lib/supabase.js';
import * as Demo from '../demo/demo.js';

/**
 * Substitui ITENS_FECHAMENTO, CLOSING_PROGRESS, CHECKLIST_STEPS e HISTORICO
 * do protótipo — e os quatro localStorage correspondentes.
 *
 * Diferença importante de comportamento: o protótipo guardava só um contador
 * de checklist por empresa ("8 de 21"), sem saber QUAIS itens estavam feitos.
 * Aqui cada item é uma linha, então os checkboxes sobrevivem ao reload.
 */

/* ---------------------------------------------------------------- */
/* Checklist padrão (template global — 9 etapas, 26 itens)          */
/* ---------------------------------------------------------------- */

let cacheChecklist = null;

export async function checklistPadrao() {
  if (Demo.emDemonstracao) return Demo.checklist;   // guarda do modo demonstração

  if (cacheChecklist) return cacheChecklist;

  const etapas = await q(
    supabase
      .from('checklist_etapas')
      .select('id, ordem, titulo, itens:checklist_itens_padrao ( id, ordem, texto, ativo )')
      .eq('ativo', true)
      .order('ordem')
  );

  cacheChecklist = etapas.map((e) => ({
    ...e,
    itens: (e.itens || []).filter((i) => i.ativo).sort((a, b) => a.ordem - b.ordem),
  }));
  return cacheChecklist;
}

export async function totalItensChecklist() {
  const etapas = await checklistPadrao();
  return etapas.reduce((n, e) => n + e.itens.length, 0);
}

/* ---------------------------------------------------------------- */
/* Fechamentos (empresa × competência)                              */
/* ---------------------------------------------------------------- */

export async function listarFechamentos(empresaId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.fechamentos.filter((f) => f.empresa_id === empresaId)
      .sort((a, b) => b.competencia.localeCompare(a.competencia));
  }

  return q(
    supabase
      .from('fechamentos')
      .select('id, competencia, status, concluido_em')
      .eq('empresa_id', empresaId)
      .order('competencia', { ascending: false })
  );
}

/** A competência em aberto mais antiga — a que está atrasada. */
export async function fechamentoAtual(empresaId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.fechamentos.filter((f) => f.empresa_id === empresaId && f.status !== 'concluido')
      .sort((a, b) => a.competencia.localeCompare(b.competencia))[0] || null;
  }

  const linhas = await q(
    supabase
      .from('fechamentos')
      .select('id, competencia, status')
      .eq('empresa_id', empresaId)
      .neq('status', 'concluido')
      .order('competencia', { ascending: true })
      .limit(1)
  );
  return linhas[0] || null;
}

/**
 * Abre várias competências de uma vez — o caso de "peguei a empresa em
 * setembro e vou fechar de janeiro a agosto".
 *
 * Cria TODOS os meses do intervalo que caibam no período de vínculo.
 * A periodicidade da empresa é orientação da gestão, não regra: o
 * fechamento sai conforme tempo e demanda, e a equipe marca o que fez.
 */
export async function abrirCompetencias(empresaId, de, ate) {
  if (Demo.emDemonstracao) return abrirCompetenciasDemo(empresaId, de, ate);   // guarda do modo demonstração

  const { data, error } = await supabase.rpc('abrir_competencias', {
    p_empresa: empresaId,
    p_de: de,
    p_ate: ate,
  });
  if (error) throw new Error(error.message);
  return data; // quantidade criada
}

export async function concluirFechamento(fechamentoId, colaboradorId) {
  const [linha] = await q(
    supabase
      .from('fechamentos')
      .update({
        status: 'concluido',
        concluido_em: new Date().toISOString(),
        concluido_por: colaboradorId,
      })
      .eq('id', fechamentoId)
      .select()
  );
  return linha;
}

export async function resumo(fechamentoId) {
  const linhas = await q(
    supabase.from('v_fechamento_resumo').select('*').eq('fechamento_id', fechamentoId)
  );
  return linhas[0] || null;
}

/**
 * Só as competências EM ABERTO.
 *
 * Antes trazia todos os fechamentos já existentes. Com 170 empresas isso
 * cresce ~2.000 linhas por ano e o payload só aumenta com o tempo, sem que
 * nenhuma tela precise do que já foi concluído. Agora fica constante.
 */
export async function resumoDeTodos() {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.resumos().filter((r) => r.status !== 'concluido');
  }

  return q(supabase.from('v_fechamento_resumo').select('*').neq('status', 'concluido'));
}

/* ---------------------------------------------------------------- */
/* Itens de conferência                                             */
/* ---------------------------------------------------------------- */

export async function listarItens(fechamentoId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.itens.filter((i) => i.fechamento_id === fechamentoId)
      .sort((a, b) => a.ordem - b.ordem);
  }

  return q(
    supabase
      .from('conferencia_itens')
      .select('id, ordem, nome, status, obs, atualizado_em')
      .eq('fechamento_id', fechamentoId)
      .order('ordem')
      .order('nome')
  );
}

export async function adicionarItem(fechamentoId, { nome, status = 'pendente', obs = null, ordem = 0 }) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const novo = { id: 'i-demo-' + Date.now(), fechamento_id: fechamentoId, nome, status, obs, ordem };
    Demo.itens.push(novo);
    return novo;
  }

  const [linha] = await q(
    supabase
      .from('conferencia_itens')
      .insert({ fechamento_id: fechamentoId, nome, status, obs, ordem })
      .select()
  );
  return linha;
}

/**
 * O histórico NÃO é escrito aqui — quem grava é a trigger do banco.
 * Era o JS que fazia isso no protótipo, o que deixava o registro
 * dependente do navegador e passível de ser forjado pelo console.
 */
export async function alterarItem(itemId, { status, obs }, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const item = Demo.itens.find((i) => i.id === itemId);
    if (!item) return null;
    const anterior = item.status;
    if (status !== undefined) item.status = status;
    if (obs !== undefined) item.obs = obs;
    if (status !== undefined && status !== anterior) {
      const f = Demo.fechamentos.find((x) => x.id === item.fechamento_id);
      Demo.registrarHistorico({ empresaId: f.empresa_id, fechamentoId: f.id, competencia: f.competencia,
        tipo: 'item', descricao: item.nome, anterior, novo: status });
    }
    return item;
  }

  const campos = { atualizado_por: colaboradorId };
  if (status !== undefined) campos.status = status;
  if (obs !== undefined) campos.obs = obs;

  const [linha] = await q(
    supabase.from('conferencia_itens').update(campos).eq('id', itemId).select()
  );
  return linha;
}

export async function removerItem(itemId) {
  await q(supabase.from('conferencia_itens').delete().eq('id', itemId));
}

/**
 * O card "Precisa de atenção": itens que alguém marcou como travados.
 *
 * Antes isto buscava TODO item que não estivesse OK. Com o banco vazio
 * eram zero linhas; depois de abrir as competências de 2026 viraram 6.671,
 * cada uma com empresa e responsáveis aninhados — vários megabytes
 * baixados a cada abertura de tela, por pessoa. E o número não queria
 * dizer nada: item que ninguém tocou não está travado, está por fazer.
 *
 * Pela definição da gestão, pendência é o que começou e parou: o item em
 * `parcial`, ou o que tem observação escrita explicando o impedimento.
 * "A realizar" é outra conversa, e sai da contagem por empresa.
 *
 * O teto de 500 existe para o dia em que algo der muito errado: melhor a
 * tela mostrar 500 e avisar do que travar o navegador de todo mundo.
 */
const TETO_SINALIZADOS = 500;

export async function itensSinalizados() {
  if (Demo.emDemonstracao) return Demo.sinalizados();   // guarda do modo demonstração

  return q(
    supabase
      .from('conferencia_itens')
      .select(`
        id, nome, status, obs, atualizado_em,
        fechamento:fechamentos!inner (
          id, competencia, status,
          empresa:empresas!inner (
            id, nome,
            responsaveis:empresa_responsaveis ( colaborador:colaboradores ( id, nome ) )
          )
        )
      `)
      .or('status.eq.parcial,obs.not.is.null')
      .neq('fechamento.status', 'concluido')
      .order('atualizado_em', { ascending: false })
      .limit(TETO_SINALIZADOS)
  );
}

/* ---------------------------------------------------------------- */
/* Marcações do checklist                                           */
/* ---------------------------------------------------------------- */

export async function listarMarcacoes(fechamentoId) {
  if (Demo.emDemonstracao) return Demo.marcacoes.filter((m) => m.fechamento_id === fechamentoId);   // guarda do modo demonstração

  return q(
    supabase
      .from('checklist_marcacoes')
      .select('item_id, feito, feito_em, feito_por')
      .eq('fechamento_id', fechamentoId)
  );
}

export async function marcarItemChecklist(fechamentoId, itemId, feito, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    let m = Demo.marcacoes.find((x) => x.fechamento_id === fechamentoId && x.item_id === itemId);
    const anterior = m?.feito ? 'feito' : 'não feito';
    // marcação que não muda nada não vai para o histórico
    if (m && m.feito === feito) return m;
    if (m) m.feito = feito;
    else { m = { fechamento_id: fechamentoId, item_id: itemId, feito, feito_em: new Date().toISOString() }; Demo.marcacoes.push(m); }
    const f = Demo.fechamentos.find((x) => x.id === fechamentoId);
    const texto = Demo.checklist.flatMap((e) => e.itens).find((i) => i.id === itemId)?.texto ?? '';
    Demo.registrarHistorico({ empresaId: f.empresa_id, fechamentoId: f.id, competencia: f.competencia,
      tipo: 'checklist', descricao: texto, anterior, novo: feito ? 'feito' : 'não feito' });
    return m;
  }

  const [linha] = await q(
    supabase
      .from('checklist_marcacoes')
      .upsert(
        {
          fechamento_id: fechamentoId,
          item_id: itemId,
          feito,
          feito_por: colaboradorId,
          feito_em: new Date().toISOString(),
        },
        { onConflict: 'fechamento_id,item_id' }
      )
      .select()
  );
  return linha;
}

/* ---------------------------------------------------------------- */
/* Histórico (somente leitura)                                      */
/* ---------------------------------------------------------------- */

export async function historico({ empresaId = null, tipo = null, limite = 200 } = {}) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.historico
      .filter((h) => (!empresaId || h.empresa_id === empresaId) && (!tipo || h.tipo === tipo))
      .slice(0, limite);
  }

  let sel = supabase
    .from('status_historico')
    .select(`
      id, competencia, tipo, descricao, status_anterior, status_novo, criado_em,
      empresa:empresas ( id, nome ),
      colaborador:colaboradores ( id, nome )
    `)
    .order('criado_em', { ascending: false })
    .limit(limite);

  if (empresaId) sel = sel.eq('empresa_id', empresaId);
  if (tipo) sel = sel.eq('tipo', tipo);

  return q(sel);
}

/* ------------------------------------------------------------------ */
/* Versão em memória do abrir_competencias() do banco                  */
/* ------------------------------------------------------------------ */

/**
 * Espelho de competencia_no_vinculo() do banco.
 * O mês de entrada e o mês de saída CONTAM: quem saiu em 15/03 teve março
 * fechado pela Tarter.
 */
function noVinculo(competencia, desde, ate) {
  const mes = (d) => d.slice(0, 7) + '-01';
  return (!desde || competencia >= mes(desde))
      && (!ate || competencia <= mes(ate));
}

function abrirCompetenciasDemo(empresaId, de, ate) {
  const empresa = Demo.empresas.find((e) => e.id === empresaId);
  if (!empresa) throw new Error('Empresa não encontrada.');

  // Ex-cliente não gera competência nova, nem que peçam o intervalo.
  if (empresa.situacao === 'encerrado' && !empresa.cliente_ate) return 0;
  // Filial: o contábil é centralizado na matriz.
  if (empresa.matriz_id) return 0;

  let criados = 0;
  const inicio = new Date(de + 'T00:00');
  const fim = new Date(ate + 'T00:00');

  for (let d = new Date(inicio); d <= fim; d.setMonth(d.getMonth() + 1)) {
    const mes = d.getMonth() + 1;
    // Todos os meses. A periodicidade é orientação da gestão, não regra:
    // o fechamento sai conforme tempo e demanda, e quem trabalha marca o
    // que realizou.
    const competencia = `${d.getFullYear()}-${String(mes).padStart(2, '0')}-01`;
    if (!noVinculo(competencia, empresa.cliente_desde, empresa.cliente_ate)) continue;
    if (Demo.fechamentos.some((f) => f.empresa_id === empresaId && f.competencia === competencia)) continue;

    // Mais próxima QUE TENHA ITENS, preferindo as anteriores. Sem isto, ao
    // preencher meses atrasados o primeiro nasce vazio e o vazio se propaga.
    const modelo = Demo.fechamentos
      .filter((f) => f.empresa_id === empresaId && f.competencia !== competencia)
      .filter((f) => Demo.itens.some((i) => i.fechamento_id === f.id))
      .sort((a, b) => {
        const anteriorA = a.competencia < competencia ? 0 : 1;
        const anteriorB = b.competencia < competencia ? 0 : 1;
        if (anteriorA !== anteriorB) return anteriorA - anteriorB;
        const dist = (x) => Math.abs(new Date(x + 'T00:00') - new Date(competencia + 'T00:00'));
        return dist(a.competencia) - dist(b.competencia);
      })[0];

    const novo = { id: 'f-demo-' + Date.now() + '-' + criados, empresa_id: empresaId, competencia, status: 'aberto', concluido_em: null };
    Demo.fechamentos.push(novo);
    criados++;

    if (modelo) {
      Demo.itens.filter((i) => i.fechamento_id === modelo.id).forEach((i, n) =>
        Demo.itens.push({ id: 'i-demo-' + Date.now() + '-' + n, fechamento_id: novo.id, ordem: i.ordem, nome: i.nome, status: 'pendente', obs: null })
      );
    }
  }
  return criados;
}

/* ================================================================== */
/* OPERAÇÕES EM LOTE — trabalhar em várias competências de uma vez    */
/*                                                                    */
/* Atende ao caso real: pegar uma empresa atrasada e fazer janeiro a  */
/* agosto numa sentada só, sem abrir a ficha oito vezes.              */
/*                                                                    */
/* Regra combinada com a Beatriz:                                     */
/*   · status  -> aplica em TODAS as competências selecionadas        */
/*   · observação -> grava só na ÚLTIMA (mais recente) selecionada,   */
/*     porque a pendência pertence ao mês mais novo e não deve ser    */
/*     espalhada para trás.                                           */
/* ================================================================== */

/** Itens de várias competências, agrupados pelo NOME do item. */
export async function itensAgrupados(fechamentoIds) {
  if (!fechamentoIds.length) return [];

  const todos = Demo.emDemonstracao
    ? Demo.itens.filter((i) => fechamentoIds.includes(i.fechamento_id))   // guarda do modo demonstração
    : await q(
        supabase
          .from('conferencia_itens')
          .select('id, ordem, nome, status, obs, fechamento_id')
          .in('fechamento_id', fechamentoIds)
      );

  const grupos = new Map();
  todos.forEach((item) => {
    if (!grupos.has(item.nome)) {
      grupos.set(item.nome, { nome: item.nome, ordem: item.ordem, ocorrencias: [] });
    }
    grupos.get(item.nome).ocorrencias.push(item);
  });

  return [...grupos.values()]
    .sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome))
    .map((g) => {
      const statuses = new Set(g.ocorrencias.map((o) => o.status));
      return {
        ...g,
        // "misto" em vez de fingir um status único quando os meses divergem
        status: statuses.size === 1 ? [...statuses][0] : 'misto',
        // a observação exibida é a do mês mais recente entre os selecionados
        obs: g.ocorrencias.at(-1)?.obs ?? null,
        faltaEm: fechamentoIds.filter((id) => !g.ocorrencias.some((o) => o.fechamento_id === id)),
      };
    });
}

/**
 * Aplica um status a um item em todas as competências selecionadas.
 * Cria o item onde ele ainda não existir, para o lote não ficar torto.
 * A observação vai só para `fechamentoIdObs` (o mês mais recente).
 */
export async function alterarItemEmLote(
  { nome, fechamentoIds, status, obs, fechamentoIdObs },
  colaboradorId
) {
  const existentes = Demo.emDemonstracao
    ? Demo.itens.filter((i) => fechamentoIds.includes(i.fechamento_id) && i.nome === nome)   // guarda do modo demonstração
    : await q(
        supabase
          .from('conferencia_itens')
          .select('id, fechamento_id')
          .in('fechamento_id', fechamentoIds)
          .eq('nome', nome)
      );

  const porFechamento = new Map(existentes.map((i) => [i.fechamento_id, i.id]));

  for (const fechamentoId of fechamentoIds) {
    const itemId = porFechamento.get(fechamentoId);
    const ehDaObs = fechamentoId === fechamentoIdObs;

    if (itemId) {
      await alterarItem(itemId, ehDaObs ? { status, obs } : { status }, colaboradorId);
    } else {
      await adicionarItem(fechamentoId, { nome, status, obs: ehDaObs ? obs : null });
    }
  }
}

/** Marca (ou desmarca) um item do checklist em todas as competências. */
export async function marcarChecklistEmLote(fechamentoIds, itemId, feito, colaboradorId) {
  for (const fechamentoId of fechamentoIds) {
    await marcarItemChecklist(fechamentoId, itemId, feito, colaboradorId);
  }
}

/** Marcações agrupadas: quantas das competências selecionadas têm cada item feito. */
export async function marcacoesAgrupadas(fechamentoIds) {
  if (!fechamentoIds.length) return new Map();

  const linhas = Demo.emDemonstracao
    ? Demo.marcacoes.filter((m) => fechamentoIds.includes(m.fechamento_id))   // guarda do modo demonstração
    : await q(
        supabase
          .from('checklist_marcacoes')
          .select('item_id, feito, fechamento_id')
          .in('fechamento_id', fechamentoIds)
      );

  const contagem = new Map();
  linhas.filter((m) => m.feito).forEach((m) =>
    contagem.set(m.item_id, (contagem.get(m.item_id) || 0) + 1)
  );
  return contagem;
}

/** Conclui várias competências. A confirmação é responsabilidade da tela. */
export async function concluirVarios(fechamentoIds, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    fechamentoIds.forEach((id) => {
      const f = Demo.fechamentos.find((x) => x.id === id);
      if (f) { f.status = 'concluido'; f.concluido_em = new Date().toISOString(); }
    });
    return fechamentoIds.length;
  }

  await q(
    supabase
      .from('fechamentos')
      .update({
        status: 'concluido',
        concluido_em: new Date().toISOString(),
        concluido_por: colaboradorId,
      })
      .in('id', fechamentoIds)
  );
  return fechamentoIds.length;
}

/** Abertura em lote para todas as empresas (só gestão). */
export async function abrirCompetenciasTodas(de, ate) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    let total = 0;
    for (const e of Demo.empresas) total += await abrirCompetencias(e.id, de, ate);
    return total;
  }

  const { data, error } = await supabase.rpc('abrir_competencias_todas', { p_de: de, p_ate: ate });
  if (error) throw new Error(error.message);
  return (data || []).reduce((n, linha) => n + linha.criadas, 0);
}
